import React, { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, Flame, Snowflake, MapPin, Users, Instagram, Star, Building2, BookOpen, Thermometer, Clock, Mail, Heart, Brain, Shield, Zap, Droplets } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import InstagramCTA from "@/components/InstagramCTA";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import BackToTop from "@/components/BackToTop";
import NewsletterSignup from "@/components/NewsletterSignup";
import TestimonialsCarousel from "@/components/TestimonialsCarousel";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { blogService } from "@/services/blog";
import BlogCard from "@/components/blog/BlogCard";
import { Skeleton } from "@/components/ui/skeleton";
import HeroSection from "@/components/landing/HeroSection";

interface Testimonial {
  quote: string;
  role: string;
  location: string;
}

const testimonials: Testimonial[] = [
  {
    quote: "The idea of having both sauna and plunge readings on one display is exactly what we need. It would make managing our setup so much easier.",
    role: "Wild Sauna Operator",
    location: "West Coast"
  },
  {
    quote: "We love the playful design approach - the duck for the plunge pool is both practical and fun. It's exactly the kind of personality we want in our space.",
    role: "Mobile Sauna Owner",
    location: "East Coast"
  },
  {
    quote: "The simplicity of the system is key for us. No apps, no Wi-Fi needed - just straightforward monitoring that works in any location.",
    role: "Community Sauna Manager",
    location: "Midlands"
  }
];

const Index: React.FC = () => {
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Create intersection observer for scroll animations
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            entry.target.classList.add('opacity-100');
          }
        });
      },
      { threshold: 0.1 }
    );

    // Observe all sections
    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      el.classList.add('opacity-0');
      observerRef.current?.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  // Platform benefits for 'What Makes SaunaDip Different?'
  const platformBenefits = [
    {
      title: "Real-Time Data",
      description: "See live sauna and cold plunge temperatures, so you always know what to expect before you go.",
      icon: Thermometer,
      stats: "Live",
      detail: "Up-to-date readings from our community and operators"
    },
    {
      title: "Verified Reviews",
      description: "Read authentic reviews from real users and wellness enthusiasts—no fake ratings.",
      icon: Star,
      stats: "100% Verified",
      detail: "Every review is from a real visitor"
    },
    {
      title: "Curated Directory",
      description: "Discover only the best, most unique sauna and cold plunge experiences across Ireland.",
      icon: MapPin,
      stats: "Curated",
      detail: "Handpicked locations and experiences"
    },
    {
      title: "Easy Booking",
      description: "Book your next session in just a few clicks—no phone calls or emails required.",
      icon: Clock,
      stats: "Instant",
      detail: "Seamless online booking (coming soon)"
    },
    {
      title: "Local Experiences",
      description: "Support local operators and discover hidden gems in your area.",
      icon: Users,
      stats: "Local",
      detail: "Connect with independent and community-run spots"
    },
    {
      title: "Community Connection",
      description: "Join a growing community of sauna and cold plunge fans, share tips, and meet like-minded people.",
      icon: Heart,
      stats: "1,200+",
      detail: "Fans and enthusiasts on our waitlist"
    }
  ];

  // Contrast therapy benefits for 'Benefits of Contrast Therapy'
  const contrastBenefits = [
    {
      title: "Enhanced Circulation",
      description: "Alternating between hot and cold stimulates blood flow, improving cardiovascular health and recovery.",
      icon: Heart,
      stats: "Up to 40%",
      detail: "Increase in blood flow during contrast therapy"
    },
    {
      title: "Mental Clarity",
      description: "Cold exposure triggers norepinephrine release, boosting focus and mental resilience.",
      icon: Brain,
      stats: "530%",
      detail: "Increase in norepinephrine during cold exposure"
    },
    {
      title: "Immune Support",
      description: "Regular sauna use and cold plunges can strengthen your body's natural defense system.",
      icon: Shield,
      stats: "2-3x",
      detail: "More frequent sauna sessions show stronger immune response"
    },
    {
      title: "Energy Boost",
      description: "Contrast therapy activates your body's natural energy systems and metabolism.",
      icon: Zap,
      stats: "24hrs",
      detail: "Sustained energy benefits after a session"
    },
    {
      title: "Temperature Adaptation",
      description: "Build resilience to both heat and cold through regular practice.",
      icon: Thermometer,
      stats: "85°C to 4°C",
      detail: "Typical temperature range for optimal benefits"
    },
    {
      title: "Stress Relief",
      description: "The combination of heat and cold helps regulate stress hormones and promotes relaxation.",
      icon: Droplets,
      stats: "30min",
      detail: "Recommended session duration for stress reduction"
    }
  ];

  const howItWorks = [
    {
      step: 1,
      title: "Find Your Spot",
      description: "Browse our directory of sauna and cold plunge locations",
      icon: MapPin
    },
    {
      step: 2,
      title: "Check Real-time Status",
      description: "View current temperatures and availability",
      icon: Thermometer
    },
    {
      step: 3,
      title: "Book & Enjoy",
      description: "Reserve your spot and experience the benefits",
      icon: Clock
    }
  ];

  // Fetch latest blog posts
  const { data: blogPostsData, isLoading: isLoadingBlogPosts } = useQuery({
    queryKey: ["latestBlogPosts"],
    queryFn: () => blogService.getPosts({ limit: 3 }),
  });

  return (
    <>
      <Helmet>
        <title>SaunaDip - Ireland & UK's First Sauna & Cold Plunge Directory</title>
        <meta name="description" content="Discover the best saunas and cold plunges across Ireland & UK with SaunaDip, the first comprehensive directory for hot and cold wellness experiences." />
        
        {/* JSON-LD Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "SaunaDip",
            "url": "https://saunadip.com",
            "description": "Ireland & UK's first comprehensive directory for sauna and cold plunge experiences",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://saunadip.com/directory?search={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "SaunaDip",
            "url": "https://saunadip.com",
            "logo": "https://saunadip.com/assets/logos/saunadip-logo.svg",
            "sameAs": [
              "https://instagram.com/saunadip",
              "https://twitter.com/saunadip"
            ],
            "description": "Ireland & UK's first comprehensive directory for sauna and cold plunge experiences",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "IE"
            }
          })}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "SaunaDip Locations",
            "description": "Discover the best sauna and cold plunge locations across Ireland",
            "itemListElement": []
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-sauna-beige">
        <BackToTop />
        {/* Hero Section */}
        <HeroSection />

        {/* How It Works Section - Moved up */}
        <section id="how-it-works" className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center animate-on-scroll">
              <h2 className="text-3xl md:text-4xl font-bold text-sauna-navy mb-6">
                How It Works
              </h2>
              <p className="text-xl text-sauna-navy/80 mb-12">
                Your journey to finding the perfect hot & cold experience in three simple steps
              </p>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="p-6 rounded-lg bg-sauna-beige/50">
                  <div className="w-12 h-12 bg-sauna-orange rounded-full flex items-center justify-center text-white font-bold text-xl mb-4 mx-auto">1</div>
                  <h3 className="text-xl font-semibold mb-2">Search & Discover</h3>
                  <p className="text-sauna-navy/70">Browse our curated directory of saunas and cold plunges near you</p>
                </div>
                <div className="p-6 rounded-lg bg-sauna-beige/50">
                  <div className="w-12 h-12 bg-sauna-orange rounded-full flex items-center justify-center text-white font-bold text-xl mb-4 mx-auto">2</div>
                  <h3 className="text-xl font-semibold mb-2">Compare & Choose</h3>
                  <p className="text-sauna-navy/70">View real-time temperatures, amenities, and authentic reviews</p>
                </div>
                <div className="p-6 rounded-lg bg-sauna-beige/50">
                  <div className="w-12 h-12 bg-sauna-orange rounded-full flex items-center justify-center text-white font-bold text-xl mb-4 mx-auto">3</div>
                  <h3 className="text-xl font-semibold mb-2">Book & Experience</h3>
                  <p className="text-sauna-navy/70">Connect with operators and book your perfect wellness session</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose SaunaDip Section */}
        <section id="why-choose" className="py-20 bg-sauna-beige">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center animate-on-scroll">
              <h2 className="text-3xl md:text-4xl font-bold text-sauna-navy mb-6">
                What Makes SaunaDip Different?
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {platformBenefits.map((benefit, index) => (
                  <Card 
                    key={benefit.title}
                    className="group hover:shadow-xl transition-all duration-300 animate-on-scroll"
                    style={{ animationDelay: `${index * 0.2}s` }}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 bg-sauna-orange/10 rounded-full group-hover:bg-sauna-orange/20 transition-colors duration-300">
                          <benefit.icon className="w-6 h-6 text-sauna-orange" />
                        </div>
                        <h3 className="text-xl font-semibold text-sauna-navy">{benefit.title}</h3>
                      </div>
                      <p className="text-sauna-navy/70 mb-4">{benefit.description}</p>
                      <div className="mt-4 pt-4 border-t border-sauna-navy/10">
                        <p className="text-sauna-orange font-semibold">{benefit.stats}</p>
                        <p className="text-sm text-sauna-navy/60">{benefit.detail}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-sauna-beige">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-sauna-navy mb-4 animate-on-scroll">
                Benefits of Contrast Therapy
              </h2>
              <p className="text-xl text-sauna-navy/80 mb-8 animate-on-scroll">
                Experience the transformative power of alternating between hot sauna and cold plunge
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {contrastBenefits.map((benefit, index) => (
                <Card 
                  key={benefit.title}
                  className="group hover:shadow-xl transition-all duration-300 animate-on-scroll overflow-hidden bg-white/80"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 bg-gradient-to-br from-sauna-orange/10 to-sauna-blue/10 rounded-full group-hover:from-sauna-orange/20 group-hover:to-sauna-blue/20 transition-colors duration-300">
                        <benefit.icon className="w-6 h-6 text-sauna-orange" />
                      </div>
                      <h3 className="text-xl font-semibold text-sauna-navy">{benefit.title}</h3>
                    </div>
                    <p className="text-sauna-navy/70 mb-4">{benefit.description}</p>
                    <div className="mt-4 pt-4 border-t border-sauna-navy/10">
                      <p className="text-sauna-orange font-semibold">{benefit.stats}</p>
                      <p className="text-sm text-sauna-navy/60">{benefit.detail}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="text-center mt-12">
              <Button 
                className="bg-sauna-navy hover:bg-sauna-navy/90 text-white px-8 py-6 text-lg rounded-full transform transition-all duration-300 hover:scale-105 hover:shadow-lg"
                onClick={() => window.location.href = '/blog/science-behind-contrast-therapy'}
              >
                Learn More About the Science
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-20 bg-sauna-beige">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-sauna-navy text-center mb-4 animate-on-scroll">
              Early Community Feedback
            </h2>
            <p className="text-lg text-sauna-navy/70 text-center mb-12 max-w-2xl mx-auto">
              We're gathering insights from sauna operators across Ireland. Here's what they're saying about our initial ideas:
            </p>
            <TestimonialsCarousel testimonials={testimonials} />
          </div>
        </section>

        {/* Blog Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-sauna-navy text-center mb-12 animate-on-scroll">
              Latest from Our Blog
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {isLoadingBlogPosts ? (
                // Loading skeletons
                Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="space-y-4 animate-on-scroll" style={{ animationDelay: `${index * 0.2}s` }}>
                    <Skeleton className="aspect-video w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                ))
              ) : blogPostsData?.data && blogPostsData.data.length > 0 ? (
                // Blog posts
                blogPostsData.data.map((post, index) => (
                  <div
                    key={post.id}
                    className="animate-on-scroll"
                    style={{ animationDelay: `${index * 0.2}s` }}
                  >
                    <BlogCard post={post} />
                  </div>
                ))
              ) : (
                // No posts found
                <div className="col-span-3 text-center py-12">
                  <p className="text-sauna-navy/70">No blog posts available at the moment.</p>
                </div>
              )}
            </div>
            {blogPostsData?.data && blogPostsData.data.length > 0 && (
              <div className="text-center mt-12">
                <Button
                  className="bg-sauna-navy hover:bg-sauna-navy/90 text-white px-8 py-6 text-lg rounded-full transform transition-all duration-300 hover:scale-105 hover:shadow-lg"
                  onClick={() => window.location.href = '/blog'}
                >
                  View All Articles
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Join as Operator Section */}
        <section className="py-20 bg-sauna-navy text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center animate-on-scroll">
              <Building2 className="w-12 h-12 text-sauna-orange mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Help Shape the Future of Sauna Tech
              </h2>
              <p className="text-xl text-white/80 mb-8">
                We're building a new platform designed just for sauna operators. Share your feedback and get rewarded with early access, free listing, or exclusive SaunaDip swag.
              </p>
              <Button 
                className="bg-sauna-orange hover:bg-sauna-orange/90 text-white px-8 py-6 text-lg rounded-full transform transition-all duration-300 hover:scale-105 hover:shadow-lg"
                onClick={() => window.location.href = '/operator-survey'}
              >
                Take the Survey →
              </Button>
              <p className="text-sm text-white/60 mt-4">
                🔒 Your info is safe. We'll never share your email or personal details.
              </p>
            </div>
          </div>
        </section>

        {/* Notify Me Form Section */}
        <section id="notify-me-form" className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center animate-on-scroll">
              <h2 className="text-3xl md:text-4xl font-bold text-sauna-navy mb-6">
                Be the First to Know
              </h2>
              <p className="text-xl text-sauna-navy/80 mb-8">
                Join our waitlist to get early access to new features and exclusive offers
              </p>
              <div className="bg-sauna-beige/50 p-8 rounded-2xl">
                <NewsletterSignup />
              </div>
            </div>
          </div>
        </section>

        {/* Enhance Instagram CTA section */}
        <section className="py-20 bg-sauna-beige">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="relative flex flex-col md:flex-row items-center gap-6 md:gap-10 bg-gradient-to-br from-white via-sauna-beige/80 to-white rounded-2xl shadow-xl p-8 md:p-14 border border-sauna-navy/10 overflow-hidden">
                {/* Decorative Background */}
                <div className="absolute -top-10 -left-10 w-40 h-40 bg-gradient-to-br from-sauna-orange/20 to-sauna-blue/10 rounded-full blur-2xl opacity-60 z-0"></div>
                <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tr from-sauna-blue/20 to-sauna-orange/10 rounded-full blur-2xl opacity-50 z-0"></div>
                {/* Logo/Avatar */}
                <div className="flex-shrink-0 flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-sauna-orange/30 to-sauna-blue/20 shadow-lg border-4 border-white z-10">
                  <img src="/assets/logos/logo-icon.svg" alt="SaunaDip Logo" className="w-16 h-16 rounded-full object-cover" />
                </div>
                {/* Text & Button */}
                <div className="flex-1 text-center md:text-left z-10">
                  <h2 className="text-3xl font-extrabold text-sauna-navy mb-2 tracking-tight drop-shadow-sm">Follow Our Journey</h2>
                  <p className="text-lg text-sauna-navy/80 mb-6 font-medium">Get exclusive updates, wellness tips, and be the first to know about new features and sauna locations.</p>
                  <div className="flex justify-center md:justify-start">
                    <button
                      onClick={() => window.open('https://instagram.com/saunadip', '_blank')}
                      className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-sauna-orange to-sauna-blue text-white text-lg font-bold shadow-xl hover:from-sauna-blue hover:to-sauna-orange transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-sauna-orange/40"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24"><path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2zm0 1.5A4.25 4.25 0 0 0 3.5 7.75v8.5A4.25 4.25 0 0 0 7.75 20.5h8.5A4.25 4.25 0 0 0 20.5 16.25v-8.5A4.25 4.25 0 0 0 16.25 3.5zm4.25 2.25a6.25 6.25 0 1 1 0 12.5 6.25 6.25 0 0 1 0-12.5zm0 1.5a4.75 4.75 0 1 0 0 9.5 4.75 4.75 0 0 0 0-9.5zm5.25 1.25a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0z"/></svg>
                      Follow on Instagram
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Index;
